import { useState, useEffect, useRef, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import OnboardingPage from "./pages/OnboardingPage";
import FeedPage from "./pages/FeedPage";
import HistoryPage from "./pages/HistoryPage";
import SettingsPage from "./pages/SettingsPage";
import ShortlistPage from "./pages/ShortlistPage";
import StarredPage from "./pages/StarredPage";
import CarouselPage from "./pages/CarouselPage";
import PricingPage from "./pages/PricingPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import ComparePage from "./pages/ComparePage";
import PostIdeasPage from "./pages/PostIdeasPage";
import PostIdeasIndexPage from "./pages/PostIdeasIndexPage";
import SignupPage from "./pages/SignupPage";
import LoginPage from "./pages/LoginPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with credentials
const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Mints a short-lived, narrowly-scoped token for the Chrome extension and
// stores it for content-scripts/app-sync.js to pick up — replaces the old
// approach of mirroring the raw, long-lived session token into localStorage.
const syncExtensionToken = async () => {
  try {
    const { data } = await api.post("/extension/token");
    localStorage.setItem("extension_token", data.extension_token);
  } catch {
    // Non-fatal — extension sync is best-effort
  }
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider Component
const AuthProvider = ({ children }) => {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [isAuthenticated, setIsAuthenticated] = useState(!!location.state?.user);
  const [isLoading, setIsLoading] = useState(!location.state?.user);
  const [subscription, setSubscription] = useState(null);

  const fetchSubscription = async () => {
    try {
      const res = await api.get("/billing/status");
      setSubscription(res.data);
    } catch {
      // Non-fatal — subscription state defaults to null
    }
  };

  useEffect(() => {
    // If we have user data from location state (e.g. from AuthCallback), use it
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      setIsLoading(false);
      fetchSubscription();
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await api.get("/auth/me");
        const { user_data, has_completed_onboarding } = response.data;

        if (user_data) {
          localStorage.setItem("user_data", JSON.stringify(user_data));
          syncExtensionToken();
        }

        // Merge user_data with has_completed_onboarding for consistent state
        setUser({ ...user_data, has_completed_onboarding });
        setIsAuthenticated(true);
        await fetchSubscription();
      } catch (error) {
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh the extension token periodically while the tab stays open, well
  // ahead of its 24h expiry — the extension picks up the new value via its own
  // localStorage poll without the user needing to do anything.
  useEffect(() => {
    if (!isAuthenticated) return;
    const interval = setInterval(syncExtensionToken, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const logout = async () => {
    try {
      // Also revokes the extension token server-side (see backend /auth/logout)
      await api.post("/auth/logout");
    } finally {
      // Clear session data from localStorage
      localStorage.removeItem("extension_token");
      localStorage.removeItem("user_data");

      setUser(null);
      setIsAuthenticated(false);
      setSubscription(null);
      window.location.href = "/";
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, isAuthenticated, isLoading, logout, api, subscription, setSubscription, fetchSubscription }}>
      {children}
    </AuthContext.Provider>
  );
};

// Theme Context
const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
};

// Theme Provider Component
const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem("theme");
    return saved || (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light");
  });

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Shared post-login flow: stores the user profile, syncs the extension
// token, and routes based on onboarding status. Used by both the Google
// redirect callback and the email/password LoginPage — both endpoints
// return the identical {user_data, has_completed_onboarding} shape via the
// backend's shared issue_session helper.
export const completeLogin = (navigate, user_data, has_completed_onboarding) => {
  localStorage.setItem("user_data", JSON.stringify(user_data));
  syncExtensionToken();

  const fullUser = { ...user_data, has_completed_onboarding };

  if (has_completed_onboarding) {
    navigate("/feed", { replace: true, state: { user: fullUser } });
  } else {
    navigate("/onboarding", { replace: true, state: { user: fullUser } });
  }

  toast.success(`Welcome, ${user_data.name}!`);
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      // Google returns the token in the hash when using response_type=id_token
      const idTokenMatch = hash.match(/id_token=([^&]+)/);

      if (!idTokenMatch) {
        navigate("/", { replace: true });
        return;
      }

      const idToken = idTokenMatch[1];

      try {
        const response = await api.post("/auth/session", { token: idToken });
        const { user_data, has_completed_onboarding } = response.data;

        // Clear the hash from URL
        window.history.replaceState(null, "", window.location.pathname);

        completeLogin(navigate, user_data, has_completed_onboarding);
      } catch (error) {
        console.error("Auth error:", error);
        toast.error("Authentication failed. Please try again.");
        navigate("/", { replace: true });
      }
    };

    processAuth();
  }, [navigate, location]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-pulse text-muted-foreground">Authenticating...</div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, requireOnboarding = true }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Check if onboarding is required but not completed
  if (requireOnboarding && user && !user.has_completed_onboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

// App Router Component
const AppRouter = () => {
  const location = useLocation();

  // CRITICAL: Check for id_token synchronously during render to prevent race conditions
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("id_token=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute requireOnboarding={false}>
            <OnboardingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/feed"
        element={
          <ProtectedRoute>
            <FeedPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/history"
        element={
          <ProtectedRoute>
            <HistoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/shortlist"
        element={
          <ProtectedRoute>
            <ShortlistPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/starred"
        element={
          <ProtectedRoute>
            <StarredPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/carousels"
        element={
          <ProtectedRoute>
            <CarouselPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/compare" element={<ComparePage />} />
      <Route path="/post-ideas" element={<PostIdeasIndexPage />} />
      <Route path="/post-ideas/:slug" element={<PostIdeasPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <Toaster
        position="top-right"
        richColors
        closeButton
        toastOptions={{
          className: "font-sans",
        }}
      />
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
export { api, API };
